import { ChevronDown, Lock, Maximize, Minimize, Pause, Play, RotateCcw, SkipBack, SkipForward } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'

import type { VideoQualityOption } from '@/api/api'
import { QualityOptionList } from '@/components/shared/quality-option-list'
import { SpeedControl } from '@/components/shared/speed-control'
import { formatTime } from '@/lib/time-format'

type VideoControlsProps = {
  playing: boolean
  currentTime: number
  duration: number
  onPlayPause: () => void
  onSeek: (seconds: number) => void
  onSeekPreview?: (seconds: number) => void
  onNext?: () => void
  onPrev?: () => void
  onVisibilityChange?: (visible: boolean) => void
  playbackRate?: number
  onPlaybackRateChange?: (rate: number) => void
  buffered?: number
  /** 選択可能な画質一覧（空なら画質ボタンを表示しない） */
  qualityOptions?: VideoQualityOption[]
  selectedQuality?: string
  onQualityChange?: (quality: string) => void
  /** 変換中の画質と進捗 */
  qualityConversion?: { quality: string; progress: number } | null
  /** 従量課金回線（セルラー等）のときに画質ボタンへ印を付ける */
  isMeteredNetwork?: boolean
  /** 画面いっぱいに表示中か */
  isMaximized?: boolean
  onToggleMaximized?: () => void
}

const CONTROLS_TIMEOUT = 3000
// 長押し2倍速中に下方向へこの距離(px)スライドするとロック
const LOCK_SLIDE_THRESHOLD = 120
// 最大化中に下方向へこの距離(px)スワイプすると最大化を解除
const EXIT_MAXIMIZE_SWIPE_THRESHOLD = 80
// シークバー操作でこの秒数以上移動したら「元の位置に戻す」を出す
const UNDO_SEEK_THRESHOLD = 30
const UNDO_SEEK_TIMEOUT = 8000

export const VideoControls = ({
  playing,
  currentTime,
  duration,
  onPlayPause,
  onSeek,
  onSeekPreview,
  onNext,
  onPrev,
  onVisibilityChange,
  playbackRate = 1,
  onPlaybackRateChange,
  buffered = 0,
  qualityOptions = [],
  selectedQuality = 'original',
  onQualityChange,
  qualityConversion = null,
  isMeteredNetwork = false,
  isMaximized = false,
  onToggleMaximized,
}: VideoControlsProps) => {
  const [visible, setVisible] = useState(true)
  const visibleRef = useRef(true)
  const [showSpeedControl, setShowSpeedControl] = useState(false)
  const [showQualityPanel, setShowQualityPanel] = useState(false)
  const [showPlayPauseIcon, setShowPlayPauseIcon] = useState(false)
  const [showSeekFeedback, setShowSeekFeedback] = useState<'backward' | 'forward' | null>(null)
  const [isLongPressing, setIsLongPressing] = useState(false)
  const [isSpeedLocked, setIsSpeedLocked] = useState(false)
  // ロックまでのスワイプ進捗 (0〜1)
  const [slideProgress, setSlideProgress] = useState(0)
  // ロック後のインジケーター表示（一定時間後にフェードアウト）
  const [lockIndicatorVisible, setLockIndicatorVisible] = useState(false)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const iconTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const seekFeedbackTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const lastTapTimeRef = useRef<number>(0)
  const lastTapXRef = useRef<number>(0)
  const seekBarRef = useRef<HTMLDivElement>(null)
  const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const originalPlaybackRateRef = useRef<number>(playbackRate)
  const touchStartPosRef = useRef<{ x: number; y: number } | null>(null)
  // 最大化解除スワイプ判定用の開始位置（長押し判定のキャンセルでは消さない）
  const exitSwipeStartRef = useRef<{ x: number; y: number } | null>(null)
  // スワイプ中の触覚フィードバックを段階的に発火させるための最終ステップ
  const lastVibrateStepRef = useRef(0)
  const [isSeeking, setIsSeeking] = useState(false)
  const [seekPosition, setSeekPosition] = useState(0)
  // シークバー操作で大きく飛んだときに戻れるようにする直前の再生位置
  const [undoSeekTime, setUndoSeekTime] = useState<number | null>(null)
  const undoSeekTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const undoBubbleRef = useRef<HTMLDivElement>(null)
  const [undoBubbleWidth, setUndoBubbleWidth] = useState(0)
  const [undoTrackWidth, setUndoTrackWidth] = useState(0)
  // シーク中は onSeekPreview で currentTime が動くため、操作開始時点の位置を別に保持する
  const seekStartTimeRef = useRef(0)
  const currentTimeRef = useRef(currentTime)

  // タッチデバイスか判定
  const isMobile = 'ontouchstart' in window || navigator.maxTouchPoints > 0

  const progress = duration > 0 ? (isSeeking ? seekPosition : currentTime) / duration : 0

  const handleOverlayClick = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      // スマホの場合はダブルタップ判定
      if (isMobile) {
        const now = Date.now()
        const clientX = 'touches' in e ? e.changedTouches[0]?.clientX : e.clientX

        // 300ms以内のタップをダブルタップと判定
        if (now - lastTapTimeRef.current < 300 && Math.abs(clientX - lastTapXRef.current) < 50) {
          // ダブルタップ: 10秒移動
          e.preventDefault()
          e.stopPropagation()

          const rect = e.currentTarget.getBoundingClientRect()
          const isLeftSide = clientX < rect.width / 2

          if (isLeftSide) {
            // 左側タップ: 10秒戻し
            onSeek(Math.max(0, currentTime - 10))
            setShowSeekFeedback('backward')
          } else {
            // 右側タップ: 10秒送り
            onSeek(Math.min(duration, currentTime + 10))
            setShowSeekFeedback('forward')
          }

          // フィードバック表示を3000ms後に消す
          if (seekFeedbackTimeoutRef.current) {
            clearTimeout(seekFeedbackTimeoutRef.current)
          }
          seekFeedbackTimeoutRef.current = setTimeout(() => {
            setShowSeekFeedback(null)
          }, 800)

          // ダブルタップ後はタイマーをリセット
          lastTapTimeRef.current = 0
          return // Control切り替えをスキップ
        } else {
          // シングルタップ: 次のタップのために記録
          lastTapTimeRef.current = now
          lastTapXRef.current = clientX
          // シングルタップの場合はControl切り替えを続行
        }
      }

      // Control表示/非表示切り替え
      setVisible((prev) => {
        const next = !prev
        visibleRef.current = next
        onVisibilityChange?.(next)
        // Controlを非表示にするときはSpeedControlも閉じる
        if (!next) {
          setShowSpeedControl(false)
        }
        if (next && playing) {
          if (timeoutRef.current) clearTimeout(timeoutRef.current)
          timeoutRef.current = setTimeout(() => {
            setVisible(false)
            setShowSpeedControl(false)
            onVisibilityChange?.(false)
          }, CONTROLS_TIMEOUT)
        }
        return next
      })
    },
    [isMobile, onSeek, duration, currentTime, playing, onVisibilityChange],
  )

  // 長押しで2倍速（スマホのみ）
  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (!isMobile) return

      const touch = e.touches[0]
      if (!touch) return

      touchStartPosRef.current = { x: touch.clientX, y: touch.clientY }
      exitSwipeStartRef.current =
        isMaximized && !isSpeedLocked && e.touches.length === 1 ? { x: touch.clientX, y: touch.clientY } : null

      // ロック中は長押しで解除
      if (isSpeedLocked) {
        longPressTimerRef.current = setTimeout(() => {
          navigator.vibrate?.(40)
          onPlaybackRateChange?.(originalPlaybackRateRef.current)
          setIsSpeedLocked(false)
          setIsLongPressing(false)
          setSlideProgress(0)
          setLockIndicatorVisible(false)
          lastVibrateStepRef.current = 0
        }, 500)
        return
      }

      // 500ms後に長押しと判定
      longPressTimerRef.current = setTimeout(() => {
        originalPlaybackRateRef.current = playbackRate
        onPlaybackRateChange?.(2)
        setIsLongPressing(true)
      }, 500)
    },
    [isMobile, isMaximized, isSpeedLocked, playbackRate, onPlaybackRateChange],
  )

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (!isMobile || !touchStartPosRef.current) return

      const touch = e.touches[0]
      if (!touch) return

      const dx = touch.clientX - touchStartPosRef.current.x
      const dy = touch.clientY - touchStartPosRef.current.y

      // ロック中: 指を動かしたら長押し解除をキャンセル
      if (isSpeedLocked) {
        if (Math.abs(dx) > 10 || Math.abs(dy) > 10) {
          if (longPressTimerRef.current) {
            clearTimeout(longPressTimerRef.current)
            longPressTimerRef.current = null
          }
        }
        return
      }

      // 長押し成立前: 10px以上移動したら長押しをキャンセル（スクロールと判定）
      if (!isLongPressing) {
        if (Math.abs(dx) > 10 || Math.abs(dy) > 10) {
          if (longPressTimerRef.current) {
            clearTimeout(longPressTimerRef.current)
            longPressTimerRef.current = null
          }
          touchStartPosRef.current = null
        }
        return
      }

      // 長押し成立後: 下方向へのスライド量に応じて進捗とバイブレーションを更新
      const progress = Math.max(0, Math.min(1, dy / LOCK_SLIDE_THRESHOLD))
      setSlideProgress(progress)

      // 0.2刻みで軽い振動フィードバックを発火
      const step = Math.floor(progress / 0.2)
      if (step > lastVibrateStepRef.current && progress < 1) {
        lastVibrateStepRef.current = step
        navigator.vibrate?.(10)
      }

      // しきい値に到達したらロック（確定時は強めの振動）
      if (dy > LOCK_SLIDE_THRESHOLD) {
        navigator.vibrate?.(40)
        setIsSpeedLocked(true)
        setSlideProgress(1)
        setLockIndicatorVisible(true)
      }
    },
    [isMobile, isLongPressing, isSpeedLocked],
  )

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      if (!isMobile) return

      // 最大化中の下スワイプで最大化を解除（長押し2倍速・パネル操作中は除外）
      const exitSwipeStart = exitSwipeStartRef.current
      exitSwipeStartRef.current = null
      const endTouch = e.changedTouches[0]
      if (exitSwipeStart && endTouch && !isLongPressing && !showSpeedControl && !showQualityPanel) {
        const dx = endTouch.clientX - exitSwipeStart.x
        const dy = endTouch.clientY - exitSwipeStart.y
        if (dy > EXIT_MAXIMIZE_SWIPE_THRESHOLD && dy > Math.abs(dx) * 1.5) {
          navigator.vibrate?.(10)
          onToggleMaximized?.()
        }
      }

      // 長押しタイマーをキャンセル
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current)
        longPressTimerRef.current = null
      }

      // ロック中は速度を維持（指を離しても2倍速継続）
      if (isSpeedLocked) {
        touchStartPosRef.current = null
        return
      }

      // 長押し中だが未ロックなら元の速度に戻す
      if (isLongPressing) {
        onPlaybackRateChange?.(originalPlaybackRateRef.current)
        setIsLongPressing(false)
      }

      // スワイプ進捗をリセット
      setSlideProgress(0)
      lastVibrateStepRef.current = 0
      touchStartPosRef.current = null
    },
    [
      isMobile,
      isLongPressing,
      isSpeedLocked,
      showSpeedControl,
      showQualityPanel,
      onToggleMaximized,
      onPlaybackRateChange,
    ],
  )

  // 再生中は自動非表示タイマー、一時停止中はタイマー停止（手動トグル可能）
  // 巻き戻し吹き出しはシークバー上に出るため、表示中はコントロールを隠さない
  useEffect(() => {
    visibleRef.current = visible
    if (!playing || undoSeekTime !== null) {
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
    } else if (visible) {
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
      timeoutRef.current = setTimeout(() => {
        setVisible(false)
        setShowSpeedControl(false)
        onVisibilityChange?.(false)
      }, CONTROLS_TIMEOUT)
    }
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
      if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current)
    }
  }, [playing, visible, undoSeekTime, onVisibilityChange])

  // ロック確定後、一定時間でインジケーターをフェードアウト
  // コントロールの自動非表示など他の状態変化でタイマーが消されないよう独立させる
  useEffect(() => {
    if (!lockIndicatorVisible) return
    const timer = setTimeout(() => setLockIndicatorVisible(false), 1500)
    return () => clearTimeout(timer)
  }, [lockIndicatorVisible])

  // 長押し中でない時の速度変更を追跡
  useEffect(() => {
    if (!isLongPressing) {
      originalPlaybackRateRef.current = playbackRate
    }
  }, [playbackRate, isLongPressing])

  // ロック中に手動で速度変更（2倍速以外）されたらロックを解除
  useEffect(() => {
    if (isSpeedLocked && playbackRate !== 2) {
      originalPlaybackRateRef.current = playbackRate
      setIsSpeedLocked(false)
      setIsLongPressing(false)
      setSlideProgress(0)
      setLockIndicatorVisible(false)
      lastVibrateStepRef.current = 0
    }
  }, [playbackRate, isSpeedLocked])

  const calcSeekPosition = useCallback(
    (clientX: number) => {
      if (!seekBarRef.current || duration <= 0) return 0
      const rect = seekBarRef.current.getBoundingClientRect()
      const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width))
      return ratio * duration
    },
    [duration],
  )

  const handleSeekStart = useCallback(
    (clientX: number) => {
      setIsSeeking(true)
      seekStartTimeRef.current = currentTimeRef.current
      setSeekPosition(calcSeekPosition(clientX))
      // タイマーリセット
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
      if (playing) {
        timeoutRef.current = setTimeout(() => {
          setVisible(false)
          setShowSpeedControl(false)
          onVisibilityChange?.(false)
        }, CONTROLS_TIMEOUT)
      }
    },
    [calcSeekPosition, playing, onVisibilityChange],
  )

  const handleSeekMove = useCallback(
    (clientX: number) => {
      if (!isSeeking) return
      const newPosition = calcSeekPosition(clientX)
      setSeekPosition(newPosition)
      // シーク中にリアルタイムでビデオをその位置にシーク（プレビュー）
      onSeekPreview?.(newPosition)
    },
    [isSeeking, calcSeekPosition, onSeekPreview],
  )

  const handleSeekEnd = useCallback(() => {
    if (!isSeeking) return
    setIsSeeking(false)
    onSeek(seekPosition)
    const jumped = Math.abs(seekPosition - seekStartTimeRef.current) >= UNDO_SEEK_THRESHOLD
    if (jumped) {
      setUndoSeekTime(seekStartTimeRef.current)
      if (undoSeekTimerRef.current) clearTimeout(undoSeekTimerRef.current)
      undoSeekTimerRef.current = setTimeout(() => setUndoSeekTime(null), UNDO_SEEK_TIMEOUT)
    }
    // タイマーリセット
    if (timeoutRef.current) clearTimeout(timeoutRef.current)
    if (playing && !jumped) {
      timeoutRef.current = setTimeout(() => {
        setVisible(false)
        setShowSpeedControl(false)
        onVisibilityChange?.(false)
      }, CONTROLS_TIMEOUT)
    }
  }, [isSeeking, seekPosition, onSeek, playing, onVisibilityChange])

  // グローバルタッチ/マウスイベント for seeking
  useEffect(() => {
    if (!isSeeking) return

    const handleMove = (e: TouchEvent | MouseEvent) => {
      const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX
      handleSeekMove(clientX)
    }
    const handleEnd = () => handleSeekEnd()

    window.addEventListener('touchmove', handleMove, { passive: true })
    window.addEventListener('touchend', handleEnd)
    window.addEventListener('mousemove', handleMove)
    window.addEventListener('mouseup', handleEnd)

    return () => {
      window.removeEventListener('touchmove', handleMove)
      window.removeEventListener('touchend', handleEnd)
      window.removeEventListener('mousemove', handleMove)
      window.removeEventListener('mouseup', handleEnd)
    }
  }, [isSeeking, handleSeekMove, handleSeekEnd])

  // シーク中は currentTime が prop 経由で動くため、操作開始位置の記録用に最新値を保持しておく
  useEffect(() => {
    if (isSeeking) return
    currentTimeRef.current = currentTime
  }, [currentTime, isSeeking])

  // 吹き出しは画面端ではみ出さない位置へ寄せるため、尖りを実位置へ合わせるには実寸が要る
  useEffect(() => {
    if (undoSeekTime === null) return
    const measure = () => {
      setUndoTrackWidth(seekBarRef.current?.getBoundingClientRect().width ?? 0)
      setUndoBubbleWidth(undoBubbleRef.current?.getBoundingClientRect().width ?? 0)
    }
    measure()
    window.addEventListener('resize', measure)
    return () => window.removeEventListener('resize', measure)
  }, [undoSeekTime])

  useEffect(() => {
    return () => {
      if (undoSeekTimerRef.current) clearTimeout(undoSeekTimerRef.current)
    }
  }, [])

  const handleUndoSeek = useCallback(() => {
    if (undoSeekTime === null) return
    onSeek(undoSeekTime)
    setUndoSeekTime(null)
    if (undoSeekTimerRef.current) clearTimeout(undoSeekTimerRef.current)
  }, [undoSeekTime, onSeek])

  const displayTime = isSeeking ? seekPosition : currentTime
  const undoSeekRatio = undoSeekTime !== null && duration > 0 ? Math.min(1, Math.max(0, undoSeekTime / duration)) : 0
  const undoTargetX = undoSeekRatio * undoTrackWidth
  const undoBubbleHalf = undoBubbleWidth / 2
  // 本体はバー内に収め、尖りだけを実位置（本体の角丸を避けた範囲）へずらす
  const undoBubbleX = Math.min(
    Math.max(undoTargetX, undoBubbleHalf),
    Math.max(undoBubbleHalf, undoTrackWidth - undoBubbleHalf),
  )
  const undoArrowX = Math.min(
    Math.max(undoTargetX - undoBubbleX + undoBubbleHalf, 12),
    Math.max(12, undoBubbleWidth - 12),
  )

  // 画面いっぱい表示の切り替え（ブラウザの全画面は使わない）
  const toggleMaximized = useCallback(() => {
    onToggleMaximized?.()
    // タイマーリセット
    if (timeoutRef.current) clearTimeout(timeoutRef.current)
    if (playing) {
      timeoutRef.current = setTimeout(() => {
        setVisible(false)
        setShowSpeedControl(false)
        onVisibilityChange?.(false)
      }, CONTROLS_TIMEOUT)
    }
  }, [onToggleMaximized, playing, onVisibilityChange])

  // 再生速度コントロールの表示切り替え
  const toggleSpeedControl = useCallback(() => {
    setShowQualityPanel(false)
    setShowSpeedControl((prev) => !prev)
    // タイマーリセット
    if (timeoutRef.current) clearTimeout(timeoutRef.current)
    if (playing) {
      timeoutRef.current = setTimeout(() => {
        setVisible(false)
        setShowSpeedControl(false)
        onVisibilityChange?.(false)
      }, CONTROLS_TIMEOUT)
    }
  }, [playing, onVisibilityChange])

  // 画質パネルの表示切り替え（コントロール非表示中は自動で閉じる）
  const toggleQualityPanel = useCallback(() => {
    setShowSpeedControl(false)
    setShowQualityPanel((prev) => !prev)
  }, [])

  useEffect(() => {
    if (!visible) setShowQualityPanel(false)
  }, [visible])

  const currentQualityOption = qualityOptions.find((option) => option.quality === selectedQuality)
  const qualityButtonLabel = currentQualityOption
    ? `${Math.min(currentQualityOption.width, currentQualityOption.height)}p`
    : '画質'

  const handleQualitySelect = useCallback(
    (quality: string) => {
      setShowQualityPanel(false)
      onQualityChange?.(quality)
    },
    [onQualityChange],
  )

  // 速度変更ハンドラ
  const handleSpeedChange = useCallback(
    (speed: number) => {
      onPlaybackRateChange?.(speed)
      // タイマーリセット
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
      if (playing) {
        timeoutRef.current = setTimeout(() => {
          setVisible(false)
          setShowSpeedControl(false)
          onVisibilityChange?.(false)
        }, CONTROLS_TIMEOUT)
      }
    },
    [onPlaybackRateChange, playing, onVisibilityChange],
  )

  // 再生/一時停止ハンドラ
  const handlePlayPause = useCallback(() => {
    onPlayPause()
    // Control非表示時はアイコンを一時表示
    if (!visibleRef.current) {
      setShowPlayPauseIcon(true)
    }
    // タイマーリセット
    if (timeoutRef.current) clearTimeout(timeoutRef.current)
    if (playing) {
      timeoutRef.current = setTimeout(() => {
        setVisible(false)
        visibleRef.current = false
        setShowSpeedControl(false)
        onVisibilityChange?.(false)
      }, CONTROLS_TIMEOUT)
    }
  }, [onPlayPause, playing, onVisibilityChange])

  // アイコン自動非表示管理
  useEffect(() => {
    if (showPlayPauseIcon) {
      if (iconTimeoutRef.current) {
        clearTimeout(iconTimeoutRef.current)
      }
      // 400ms表示した後、300msかけてフェードアウト&スケールダウン
      iconTimeoutRef.current = setTimeout(() => {
        setShowPlayPauseIcon(false)
      }, 400)
    }
    return () => {
      if (iconTimeoutRef.current) {
        clearTimeout(iconTimeoutRef.current)
      }
    }
  }, [showPlayPauseIcon])

  return (
    <div
      className="absolute inset-0 z-10 select-none"
      onClick={handleOverlayClick}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Controls overlay */}
      <div
        className={`absolute inset-0 flex flex-col justify-end transition-opacity duration-200 ${
          visible ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
      >
        {/* 中央のコントロールボタン群 */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="flex items-center gap-10">
            {/* Control非表示時は他のボタンを無効化、再生/一時停止のみ有効 */}
            <div className={`contents ${visible ? 'pointer-events-auto' : 'pointer-events-none'}`}>
              {/* 前エピソード */}
              <button
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  onPrev?.()
                  // タイマーリセット
                  if (timeoutRef.current) clearTimeout(timeoutRef.current)
                  if (playing) {
                    timeoutRef.current = setTimeout(() => {
                      setVisible(false)
                      setShowSpeedControl(false)
                      onVisibilityChange?.(false)
                    }, CONTROLS_TIMEOUT)
                  }
                }}
                disabled={!onPrev}
                className={`p-2 transition-opacity drop-shadow-[0_2px_4px_rgba(0,0,0,0.5)] ${
                  onPrev ? 'opacity-80 hover:opacity-100 active:scale-90' : 'opacity-30'
                }`}
              >
                <SkipBack className="size-6 text-white" fill="white" />
              </button>

              {/* 10秒戻し - PCのみ */}
              {!isMobile && (
                <button
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    onSeek(Math.max(0, currentTime - 10))
                    // タイマーリセット
                    if (timeoutRef.current) clearTimeout(timeoutRef.current)
                    if (playing) {
                      timeoutRef.current = setTimeout(() => {
                        setVisible(false)
                        setShowSpeedControl(false)
                        onVisibilityChange?.(false)
                      }, CONTROLS_TIMEOUT)
                    }
                  }}
                  className="p-1 drop-shadow-[0_2px_4px_rgba(0,0,0,0.5)] opacity-90 hover:opacity-100 active:scale-90 transition-all"
                >
                  <svg viewBox="0 0 24 24" className="size-10 text-white" fill="currentColor">
                    <path d="M11.5 3c5.24 0 9.5 4.26 9.5 9.5S16.74 22 11.5 22 2 17.74 2 12.5h2a7.5 7.5 0 1 0 2.2-5.3L9 10H2V3l2.8 2.8A9.46 9.46 0 0 1 11.5 3z" />
                    <text x="12" y="15.5" textAnchor="middle" fontSize="7" fontWeight="bold" fill="currentColor">
                      10
                    </text>
                  </svg>
                </button>
              )}
            </div>

            {/* 再生/一時停止 - 常に有効 */}
            <button
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                handlePlayPause()
              }}
              className="p-2 drop-shadow-[0_2px_4px_rgba(0,0,0,0.5)] opacity-90 hover:opacity-100 active:scale-90 transition-all pointer-events-auto touch-manipulation"
            >
              {playing ? (
                <Pause className="size-12 text-white" fill="white" />
              ) : (
                <Play className="size-12 text-white ml-0.5" fill="white" />
              )}
            </button>

            <div className={`contents ${visible ? 'pointer-events-auto' : 'pointer-events-none'}`}>
              {/* 10秒送り - PCのみ */}
              {!isMobile && (
                <button
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    onSeek(Math.min(duration, currentTime + 10))
                    // タイマーリセット
                    if (timeoutRef.current) clearTimeout(timeoutRef.current)
                    if (playing) {
                      timeoutRef.current = setTimeout(() => {
                        setVisible(false)
                        setShowSpeedControl(false)
                        onVisibilityChange?.(false)
                      }, CONTROLS_TIMEOUT)
                    }
                  }}
                  className="p-1 drop-shadow-[0_2px_4px_rgba(0,0,0,0.5)] opacity-90 hover:opacity-100 active:scale-90 transition-all"
                >
                  <svg viewBox="0 0 24 24" className="size-10 text-white" fill="currentColor">
                    <path d="M12.5 3C7.26 3 3 7.26 3 12.5S7.26 22 12.5 22 22 17.74 22 12.5h-2a7.5 7.5 0 1 1-2.2-5.3L15 10h7V3l-2.8 2.8A9.46 9.46 0 0 0 12.5 3z" />
                    <text x="12" y="15.5" textAnchor="middle" fontSize="7" fontWeight="bold" fill="currentColor">
                      10
                    </text>
                  </svg>
                </button>
              )}

              {/* 次エピソード */}
              <button
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  onNext?.()
                  // タイマーリセット
                  if (timeoutRef.current) clearTimeout(timeoutRef.current)
                  if (playing) {
                    timeoutRef.current = setTimeout(() => {
                      setVisible(false)
                      setShowSpeedControl(false)
                      onVisibilityChange?.(false)
                    }, CONTROLS_TIMEOUT)
                  }
                }}
                disabled={!onNext}
                className={`p-2 transition-opacity drop-shadow-[0_2px_4px_rgba(0,0,0,0.5)] ${
                  onNext ? 'opacity-80 hover:opacity-100 active:scale-90' : 'opacity-30'
                }`}
              >
                <SkipForward className="size-6 text-white" fill="white" />
              </button>
            </div>
          </div>
        </div>

        {/* 速度コントロール - Control表示時のみ */}
        {visible && showSpeedControl && onPlaybackRateChange && (
          <div
            className="absolute bottom-16 left-0 right-0 flex justify-center pointer-events-none"
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="bg-zinc-900/95 rounded-lg p-4 w-[90%] pointer-events-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <SpeedControl
                presetSpeeds={[0.5, 0.75, 1, 1.25, 1.5, 2, 3, 4, 5, 8, 10]}
                min={0.25}
                max={10}
                step={0.05}
                playbackSpeed={playbackRate}
                onChangeSpeed={handleSpeedChange}
              />
            </div>
          </div>
        )}

        {/* 画質選択パネル - Control表示時のみ */}
        {visible && showQualityPanel && qualityOptions.length > 0 && (
          <div
            className="absolute bottom-16 left-0 right-0 flex justify-center pointer-events-none"
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="bg-zinc-900/95 rounded-lg p-3 w-[90%] max-w-md pointer-events-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="mb-2 px-1 text-xs font-medium text-white/60">画質</div>
              <QualityOptionList
                options={qualityOptions}
                selectedQuality={selectedQuality}
                onSelect={handleQualitySelect}
                conversion={qualityConversion}
                dark
              />
              {isMeteredNetwork && (
                <p className="mt-2 px-1 text-[11px] text-amber-400">
                  モバイル回線に接続中です。低い画質で通信量を節約できます。
                </p>
              )}
            </div>
          </div>
        )}

        {/* 下部コントロールバー */}
        <div className="px-4 py-2 bg-linear-to-t from-black/70 to-transparent" onClick={(e) => e.stopPropagation()}>
          {/* シークバー */}
          <div
            ref={seekBarRef}
            className="w-full h-6 flex items-center cursor-pointer group"
            onMouseDown={(e) => {
              e.stopPropagation()
              handleSeekStart(e.clientX)
            }}
            onTouchStart={(e) => {
              e.stopPropagation()
              handleSeekStart(e.touches[0].clientX)
            }}
          >
            <div className="w-full h-1 bg-white/30 rounded-full relative group-hover:h-1.5 transition-all">
              {/* バッファ進捗 */}
              <div
                className="absolute h-full bg-white/40 rounded-full transition-none"
                style={{ width: `${Math.min(100, (buffered / Math.max(duration, 1)) * 100)}%` }}
              />
              {/* 再生進捗 */}
              <div className="absolute h-full bg-white/50 rounded-full" style={{ width: `${progress * 100}%` }} />
              <div
                className="absolute h-full bg-white rounded-full transition-none"
                style={{ width: `${progress * 100}%` }}
              />
              {/* シークつまみ */}
              <div
                className="absolute top-1/2 -translate-y-1/2 w-3.5 h-3.5 bg-white rounded-full shadow-md opacity-0 group-hover:opacity-100 transition-opacity"
                style={{ left: `calc(${progress * 100}% - 7px)` }}
              />
              {/* 大きく飛んだ直後だけ、元の位置を指す吹き出しで巻き戻しボタンを出す */}
              {undoSeekTime !== null && (
                <div
                  ref={undoBubbleRef}
                  className={`absolute bottom-3.5 -translate-x-1/2 z-50 animate-in fade-in slide-in-from-bottom-1 duration-200 ${
                    undoBubbleWidth > 0 ? '' : 'invisible'
                  }`}
                  style={{ left: `${undoBubbleX}px` }}
                >
                  <button
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      handleUndoSeek()
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                    onTouchStart={(e) => e.stopPropagation()}
                    onTouchEnd={(e) => e.stopPropagation()}
                    className="relative flex items-center gap-1.5 whitespace-nowrap rounded-lg border border-white/70 bg-zinc-900 px-3 py-1.5 text-white text-xs font-medium shadow-2xl active:scale-95 transition-transform"
                  >
                    <RotateCcw className="size-3.5" />
                    {formatTime(Math.floor(undoSeekTime))} に戻す
                    {/* 吹き出しの尖り: 回転した四角で本体と同色・同じ縁取りにする */}
                    <span
                      className="absolute top-full size-2 -translate-x-1/2 -translate-y-1/2 rotate-45 border-r border-b border-white/70 bg-zinc-900"
                      style={{ left: `${undoArrowX}px` }}
                    />
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* 時間表示と画面いっぱい表示ボタン */}
          <div className="flex items-center justify-between" onClick={(e) => e.stopPropagation()}>
            <span className="text-white/80 text-xs font-mono">
              {formatTime(Math.floor(displayTime))} / {formatTime(Math.floor(duration))}
            </span>
            <div className="flex items-center gap-3">
              {/* 画質ボタン */}
              {qualityOptions.length > 0 && onQualityChange && (
                <button
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    toggleQualityPanel()
                  }}
                  className="relative text-white/80 hover:text-white transition-colors px-1.5 py-0! text-xs font-medium min-w-10 text-center"
                  aria-label="Change video quality"
                >
                  {qualityConversion ? `${qualityConversion.progress}%` : qualityButtonLabel}
                  {isMeteredNetwork && (
                    <span className="absolute -top-0.5 -right-0.5 size-1.5 rounded-full bg-amber-400" />
                  )}
                </button>
              )}
              {/* 再生速度ボタン */}
              {onPlaybackRateChange && (
                <button
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    toggleSpeedControl()
                  }}
                  className="text-white/80 hover:text-white transition-colors px-1.5 py-0! text-xs font-medium min-w-10 text-center"
                  aria-label="Change playback speed"
                >
                  {playbackRate}x
                </button>
              )}
              {/* 画面いっぱい表示ボタン */}
              <button
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  toggleMaximized()
                }}
                className="text-white/80 hover:text-white transition-colors p-0!"
                aria-label={isMaximized ? '通常表示に戻す' : '画面いっぱいに表示'}
              >
                {isMaximized ? <Minimize className="size-5" /> : <Maximize className="size-5" />}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Control非表示時の一時的な再生/一時停止アイコン（表示時のみマウント: iOS Safari横向きで中央タップを intercept する不具合を回避） */}
      {!visible && showPlayPauseIcon && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-100 animate-in fade-in zoom-in-90 duration-300 ease-out">
          <div className="rounded-full p-4 bg-black/80 shadow-2xl">
            {playing ? (
              <Pause className="size-12 text-white drop-shadow-lg" fill="white" strokeWidth={0} />
            ) : (
              <Play className="size-12 text-white drop-shadow-lg" fill="white" strokeWidth={0} />
            )}
          </div>
        </div>
      )}

      {/* ダブルタップフィードバック（スマホのみ、表示時のみマウント） */}
      {isMobile && showSeekFeedback === 'backward' && (
        <div className="absolute left-1/4 top-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center justify-center pointer-events-none z-100 animate-in fade-in zoom-in-90 duration-300 ease-out">
          <div className="rounded-full p-4 bg-black/80 shadow-2xl">
            <svg viewBox="0 0 24 24" className="size-12 text-white drop-shadow-lg" fill="currentColor">
              <path d="M11.5 3c5.24 0 9.5 4.26 9.5 9.5S16.74 22 11.5 22 2 17.74 2 12.5h2a7.5 7.5 0 1 0 2.2-5.3L9 10H2V3l2.8 2.8A9.46 9.46 0 0 1 11.5 3z" />
              <text x="12" y="15.5" textAnchor="middle" fontSize="5" fontWeight="bold" fill="white">
                10
              </text>
            </svg>
          </div>
        </div>
      )}
      {isMobile && showSeekFeedback === 'forward' && (
        <div className="absolute right-1/4 top-1/2 translate-x-1/2 -translate-y-1/2 flex items-center justify-center pointer-events-none z-100 animate-in fade-in zoom-in-90 duration-300 ease-out">
          <div className="rounded-full p-4 bg-black/80 shadow-2xl">
            <svg viewBox="0 0 24 24" className="size-12 text-white drop-shadow-lg" fill="currentColor">
              <path d="M12.5 3C7.26 3 3 7.26 3 12.5S7.26 22 12.5 22 22 17.74 22 12.5h-2a7.5 7.5 0 1 1-2.2-5.3L15 10h7V3l-2.8 2.8A9.46 9.46 0 0 0 12.5 3z" />
              <text x="12" y="15.5" textAnchor="middle" fontSize="5" fontWeight="bold" fill="white">
                10
              </text>
            </svg>
          </div>
        </div>
      )}

      {/* 2倍速インジケーター（長押し中 / ロック中、スマホのみ） */}
      {isMobile && (isLongPressing || isSpeedLocked) && (
        <div
          className={`absolute top-20 left-1/2 -translate-x-1/2 z-100 pointer-events-none flex flex-col items-center gap-2 transition-opacity ease-out ${
            isSpeedLocked && !lockIndicatorVisible ? 'opacity-0 duration-1000' : 'opacity-100 duration-200'
          }`}
        >
          {isSpeedLocked ? (
            <>
              <div className="flex items-center gap-1.5 rounded-full bg-black/80 px-4 py-2 shadow-2xl">
                <Lock className="size-4 text-white" />
                <span className="text-white text-sm font-bold tabular-nums">2x</span>
              </div>
              <span className="text-white/80 text-xs">長押しで解除</span>
            </>
          ) : (
            <>
              {/* ロックアイコンを円周上の進捗リングで囲む */}
              <div className="relative flex items-center justify-center size-16 rounded-full bg-black/80 shadow-2xl">
                <svg className="absolute inset-0 size-16 -rotate-90" viewBox="0 0 64 64">
                  <circle cx="32" cy="32" r="28" fill="none" stroke="rgba(255,255,255,0.25)" strokeWidth="4" />
                  <circle
                    cx="32"
                    cy="32"
                    r="28"
                    fill="none"
                    stroke="white"
                    strokeWidth="4"
                    strokeLinecap="round"
                    strokeDasharray={2 * Math.PI * 28}
                    strokeDashoffset={2 * Math.PI * 28 * (1 - slideProgress)}
                    style={{ transition: 'stroke-dashoffset 75ms ease-out' }}
                  />
                </svg>
                <Lock className="size-5 text-white" />
              </div>
              <span className="text-white/80 text-xs flex items-center gap-0.5">
                <ChevronDown className="size-3" />
                下にスライドしてロック
              </span>
            </>
          )}
        </div>
      )}
    </div>
  )
}
